import cn from 'clsx'
import React, { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { areRatesLoading, BaseRatePackageView, getBaseRatePackagesView, isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import AlwaysOnPriceDetails from './AlwaysOnPriceDetails'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import TextButton from 'components/Luxkit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'components/Luxkit/Typography/Caption'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import RenderInclusions from './RenderInclusions'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import config from 'constants/config'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import AccommodationPackageTile from '../Accommodation/AccommodationPackageTile'
import AccommodationPackageTileContentArea from '../Accommodation/AccommodationPackageTileContentArea'
import AccommodationPackageTilePricingArea from '../Accommodation/AccommodationPackageTilePricingArea'
import AccommodationPackageTileDetails from '../Accommodation/AccommodationPackageTileDetails'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import AccommodationPackageTilePricingBlocks from '../Accommodation/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from '../Accommodation/AccommodationPackageTilePricingBlock'
import PriceRowValueDiscount from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscount'
import { checkCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { initialRooms } from 'lib/offer/occupancyUtils'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import Plural from 'components/utils/Formatters/Plural'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

interface Props {
  optionNumber: number;
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  partnership: App.Partnership;
  checkIn?: string;
  duration: number;
  rooms: Array<App.Occupants>;
  calendarsLoading?: boolean;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  previewMode: boolean;
  currency: string;
  baseRatePackagesView: Array<BaseRatePackageView>
  defaultDepositAmountPercentage: number;
  canViewLuxPlusBenefits: boolean;
  userMultiItemCartEnabled: boolean;
  isFlightsSelected?: boolean;
  showOptionLabel?: boolean;
  availableForPurchase?: boolean;
}

function AlwaysOnRateOption(props: Props) {
  const {
    offer,
    packageView,
    partnership,
    checkIn,
    duration,
    rooms,
    optionNumber,
    calendarsLoading,
    flightOrigin,
    flightDestination,
    previewMode,
    currency,
    baseRatePackagesView,
    defaultDepositAmountPercentage,
    canViewLuxPlusBenefits,
    userMultiItemCartEnabled,
    isFlightsSelected,
    showOptionLabel = true,
    availableForPurchase,
  } = props

  const {
    package: pkg,
    flightPrice,
    bundledDiscountPercent,
    hotelDiscountPercent,
  } = packageView

  const offerPageDispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const checkOut = useMemo(() => checkOutMoment?.format(ISO_DATE_FORMAT), [checkOutMoment])

  const offerAvailableRates = useOfferAvailableRates(offer.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialRooms,
  })

  const pkgAvailableRate = useMemo(() => offerAvailableRates.rates.find(rate => rate.packageUniqueKey === pkg.uniqueKey), [pkg, offerAvailableRates])
  const fetching = !!calendarsLoading || !!offerAvailableRates?.fetching
  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
  const ignoreDynamicCancellationPolicy = pkg?.ignoreDynamicCancellationPolicy ?? false
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled

  const dynamicHotelAvailable = ((useDynamicCancellationPolicies && !!pkgAvailableRate && checkIn && !fetching) || !useDynamicCancellationPolicies || !checkIn || fetching)
  const hotelAvailable = packageView.hotelAvailable && dynamicHotelAvailable
  const canPurchase = availableForPurchase && packageView.capacityValid && hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && canPurchase && !fetching && packageView.fromCalendarInfo
  const isValidRoomOccupancy = packageView.flightsAvailable || offer.bundledWithFlightsOnly
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && !(hotelAvailable && !packageView.capacityValid && availableForPurchase && !canPurchase) && isValidRoomOccupancy && config.businessTraveller.currentAccountMode !== 'business'
  const canAddToUserCart = userMultiItemCartEnabled && canBuyNow

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkInMoment && checkInMoment.diff(moment(), 'days') < minDay
  }, [checkInMoment])

  const refundable = !useDynamicCancellationPolicies && pkg.roomRate?.cancellationPolicy?.type !== 'non-refundable'

  const departureTooFarInFuture = useMemo(() => checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330, [checkInMoment])
  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooEarly

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent
  const shouldShowDiscount = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && (canPurchase || previewMode) && !pkg.roomRate?.inclusionsHideValue

  const baseRatePackageView = useMemo(() => {
    return baseRatePackagesView.find(
      (packageItem) => packageItem.duration === pkg.duration && packageItem.id === pkg.id,
    )
  }, [baseRatePackagesView, pkg.duration, pkg.id])

  const baseRateValue = canViewLuxPlusBenefits ? baseRatePackageView?.memberValue : baseRatePackageView?.value
  const inclusionsAmount = (canViewLuxPlusBenefits ? pkg.totalInclusionsAmount : pkg.inclusionsAmount) ?? 0
  const pkgValueOrPkgMemberValue = canViewLuxPlusBenefits ? pkg.memberValue : pkg.value
  const packageViewHotelValueOrMemberHotelValue = (canViewLuxPlusBenefits ? packageView.hotelMemberValue : packageView.hotelValue) ?? 0

  // We use base rate cost to calculate new totalValue and discountPercent, using the isBaseRate flag on rate plan level.
  // This shows a bigger discount
  const valuedUpTo = useMemo(() => {
    if (baseRateValue && (baseRateValue + inclusionsAmount) > pkgValueOrPkgMemberValue) {
      const displayedHotelValue = baseRateValue + inclusionsAmount + (packageView.propertyFees ?? 0)
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, packageView.hotelPrice)

      if (displayedHotelDiscountPercent > highestDiscountPercent) {
        return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
      }
    }

    return { value: packageViewHotelValueOrMemberHotelValue, percent: highestDiscountPercent }
  }, [baseRateValue, inclusionsAmount, pkgValueOrPkgMemberValue, packageViewHotelValueOrMemberHotelValue, packageView.propertyFees, packageView.hotelPrice, highestDiscountPercent])

  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, packageView.hotelTotal, packageView.package, pkgAvailableRate)

  const { inclusions, bonusInclusions, tacticalInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const showPartnership = !!packageView.hotelAvailable && !!partnership?.hasEarn

  const { travelFrom, travelTo } = useMemo(() => {
    const tacticalSchedule = pkg?.roomRate?.schedules?.find(x => x.type === 'tactical')
    let travelFrom = tacticalSchedule?.travelPeriod?.from
    let travelTo = tacticalSchedule?.travelPeriod?.to
    if ((!travelFrom || !travelTo) && tacticalInclusions.length > 0) {
      const tacticalInclusionsSchedule = tacticalInclusions[0].schedules?.find(x => x.type === 'tactical')
      travelFrom = tacticalInclusionsSchedule?.travelPeriod?.from
      travelTo = tacticalInclusionsSchedule?.travelPeriod?.to
    }

    return { travelFrom, travelTo }
  }, [pkg?.roomRate?.schedules, tacticalInclusions])

  const stayPayRateTier = useMemo(() => {
    return pkg?.stayPay?.find(tier => tier.stayNights === duration)
  }, [pkg?.stayPay, duration])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const isMobileDevice = useIsMobileDevice()
  const showHotelAndFlightsToggle = !isMobileDevice && flightsEnabled && !offer.bundledWithFlightsOnly
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !hideHotelPriceBlock,
    }),
  ], [hideHotelPriceBlock, offer, packageView])

  return (
    <AccommodationPackageTile data-testid={pkg.uniqueKey}>
      <AccommodationPackageTileContentArea>
        <AccommodationPackageTileDetails
          packageNumber={showOptionLabel ? optionNumber : undefined}
          // @ts-expect-error not App.HotelPackage
          roomPackage={packageView.package}
          tagLineLabel={packageView.package.hasTactical && isLPCTacticalEnabled() && <ProductTypeLabel productType="limited_time_special"/>}
          stayPayRateTier={stayPayRateTier}
        >
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              <TAOCancellationPolicies
                offerId={offer.id}
                useDynamicCancellationPolicies={useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy}
                timezoneOffset={offer.property?.timezoneOffset}
                uniqueKey={pkg.uniqueKey}
                checkInDate={checkIn}
                checkOutDate={checkOut}
                occupants={rooms}
                cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
                offerType={offer.type}
              />
              {!!showPartnership && pkg.partnership &&
                <OfferPackagePartnershipLabel
                  offer={offer}
                  price={packageView.hotelPrice}
                  packagePartnership={pkg.partnership}
                />}
              {packageView.package.hasTactical && isLPCTacticalEnabled() && <BodyText variant="small" weight="normal" colour="neutral-two">
                {travelFrom && travelTo && (
                  <>
                    Travel between: {moment(travelFrom).format(DMY_CASUAL_SHORT_FORMAT)} - {moment(travelTo).subtract(pkg.duration, 'days').format(DMY_CASUAL_SHORT_FORMAT)}.
                    {' '}
                  </>
                )}
                Minimum stay: <Plural singular="night" count={pkg.duration} withCount />
              </BodyText>}

              {stayPayRateTier && <BodyText variant="small" colour="neutral-one">
                Your travel dates qualify for <Plural singular="free night" count={stayPayRateTier.freeNights} withCount />
              </BodyText>}
            </Group>
          </Group>
        </AccommodationPackageTileDetails>
        <div>
          <LargeContentToggle
            maxHeight={208}
            showMoreText="View more inclusions"
            showLessText="View less inclusions"
          >
            <RenderInclusions
              bonusInclusions={bonusInclusions}
              inclusions={inclusions}
              luxPlusInclusions={luxPlusInclusions}
              offer={offer}
              stayPayRateTier={stayPayRateTier}
            />
          </LargeContentToggle>
        </div>
      </AccommodationPackageTileContentArea>
      <AccommodationPackageTilePricingArea>
        <WalledContent enforced={offer.walledGarden} wall={(
          <Group fullHeight direction="vertical" verticalAlign="end">
            <TextButton size="large" kind="secondary" onClick={onSignUpClick}>Sign up to view exclusive rates</TextButton>
          </Group>
        )}
        >
          <Group direction="horizontal" gap={4} wrap="wrap">
            {shouldShowDiscount && <PriceRowValueDiscount
              type="estimate"
              discountPercentage={valuedUpTo.percent}
              data-testid="always-on-rate-option-discount"
            />}
            {isDepositEnabled && <SecureWithDepositTagAndModal
              pkgAvailableRate={pkgAvailableRate}
              offerType={offer.type}
              depositAmountPercentage={depositAmountPercentage}
              depositThresholds={offer.depositThresholds}
            />}
          </Group>
          <AccommodationPackageTilePricingBlocks>
            {!offer.bundledWithFlightsOnly && !hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
              data-testid="pricing"
              title={(flightsEnabled && !showHotelAndFlightsToggle) ? 'Hotel' : undefined}
              callToAction={<>
                {canBuyNow && <OfferBookButton
                  size="large"
                  type="submit"
                  disabled={fetching}
                  data-testid={cn('HotelBookingBtn', 'buy-now-button', { refundable })}
                  packageView={packageView}
                >
                  Book now
                </OfferBookButton>}
                {canAddToUserCart && <OfferAddToCartButton
                  size="large"
                  type="submit"
                  disabled={fetching}
                  packageView={packageView}
                />}
                {availableForPurchase && !canPurchase && <>
                  {hotelAvailable && !packageView.capacityValid && <div>
                    <Caption variant="large" align="center">Max number of guests exceeded</Caption>
                    {!isFlightForceBundled(offer) && <TextButton
                      size="large"
                      kind="tertiary"
                      onClick={onToggleFilter}
                      data-filter="capacity"
                    >
                      Add another {offer.saleUnit}
                    </TextButton>}
                  </div>}
                  {!hotelAvailable && <Caption variant="large">Sold out for your selected dates.</Caption>}
                </>}
              </>}
            >
              {(canPurchase || previewMode) && <VerticalSpacer gap={12}>
                <div>
                  <Caption variant="medium">
                    <AlwaysOnPriceDetails
                      rooms={rooms}
                      checkIn={checkIn}
                      packageView={packageView}
                      offer={offer}
                      canBuyNow={canBuyNow}
                      valuedUpTo={valuedUpTo}
                    />
                    <LoadingIndicator visible={fetching} floating opaque />
                  </Caption>
                </div>
                <LoyaltyPoints
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={!checkIn ? 'estimate' : 'exact'}
                />
              </VerticalSpacer>}
            </AccommodationPackageTilePricingBlock>}
            {flightsEnabled && hotelAvailable && !hideHotelAndFlightsPriceBlock && <AccommodationPackageTilePricingBlock
              data-testid="pricing"
              title={(!offer.bundledWithFlightsOnly && !showHotelAndFlightsToggle) ? 'Hotel + flights' : undefined}
              callToAction={<>
                {!canAddToUserCart && canBuyNow && flightsBookable && (
                  <OfferBookButton
                    size="large"
                    disabled={fetching}
                    data-testid="HotelAndFlightsBtn"
                    data-buytype="flights"
                    packageView={packageView}
                  >
                    Book now
                  </OfferBookButton>
                )}
                {canAddToUserCart && flightsBookable && <OfferSelectPackageButton
                  size="large"
                  disabled={fetching}
                  data-buytype="flights"
                  packageView={packageView}
                />}
                {availableForPurchase && !canPurchase && !hotelAvailable && <BodyText variant="medium">
                  Sold out for your selected dates.
                </BodyText>}
                {!hasFlights && flightOrigin.code === flightDestination?.code && <TextButton
                  size="large"
                  data-filter="airport"
                  kind="tertiary"
                  onClick={onToggleFilter}
                >
                  Change airport
                </TextButton>}
                {flightOrigin.code !== flightDestination?.code && departureTooEarly && <TextButton
                  size="large"
                  data-filter="checkin"
                  kind="tertiary"
                  onClick={onToggleFilter}
                >
                  Change departure date
                </TextButton>}
              </>}
            >
              <VerticalSpacer gap={12} >
                <div>
                  <VerticalSpacer gap={12}>
                    {!hasFlights && flightOrigin.code === flightDestination?.code && <BodyText
                      variant="small"
                      colour="neutral-two"
                    >
                      No flights available from {flightOrigin.name}. Please select another departing airport.
                    </BodyText>}
                    {!hasFlights && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture && !departureTooEarly && (
                      <BodyText variant="small" colour="neutral-two">
                        Add flights to the hotel package. Select from all major airlines at the next step.
                      </BodyText>
                    )}
                    {!hasFlights && departureTooFarInFuture && (
                      <BodyText variant="small" colour="neutral-two">
                        Flights can only be booked up to 11 months in advance.
                      </BodyText>
                    )}
                    {flightOrigin.code !== flightDestination?.code && departureTooEarly && <BodyText
                      variant="small"
                      colour="neutral-two"
                    >
                      Flights cannot be booked online when check-in is within 24 hours
                    </BodyText>}
                  </VerticalSpacer>
                  {((hasFlights && !departureTooEarly) || (hasFlights && previewMode)) && (
                    <Caption variant="medium" data-testid="pricing">
                      <AlwaysOnPriceDetails
                        rooms={rooms}
                        checkIn={checkIn}
                        packageView={packageView}
                        offer={offer}
                        canBuyNow={canBuyNow}
                        isFlightPrice
                        valuedUpTo={valuedUpTo}
                      />
                      <LoadingIndicator visible={fetching} floating opaque />
                    </Caption>
                  )}
                  <AtolProtectedTooltip />
                </div>
                <LoyaltyPoints
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType="estimate"
                />
              </VerticalSpacer>
            </AccommodationPackageTilePricingBlock>}
          </AccommodationPackageTilePricingBlocks>
          <PriceRowAgentHubCommission
            size="M"
            offerId={offer.id}
            productType={offer.productType}
            offerTotal={packageView.hotelPrice}
            packageId={pkg.id}
          />
        </WalledContent>
      </AccommodationPackageTilePricingArea>
    </AccommodationPackageTile>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  calendarsLoading: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey] || areRatesLoading(appState, state),
  previewMode: appState.system.previewMode,
  flightOrigin: state.airport,
  currency: appState.geo.currentCurrency,
  baseRatePackagesView: getBaseRatePackagesView(appState, state),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isFlightsSelected: state.flights,
  canViewLuxPlusBenefits: checkCanViewLuxPlusBenefits(appState),
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), AlwaysOnRateOption, preventRenderWithOpenFilter)
